// External Imports
import React, { useContext } from 'react';
import { Field, change } from 'redux-form';
import PropTypes from 'prop-types';

// PPUI Imports
import { BodyText } from '@paypalcorp/pp-react-text';
import { Alert } from '@paypalcorp/pp-react-alert';
import { MessageFormat } from '@paypalcorp/worldready';
import { Context as WorldReadyContext } from '@paypalcorp/worldready-react';

// Internal Imports
import { BTN_FACTORY_V3_FORM } from '../../../clientConstants';
import BasicRadioField from '../../../shared/fields/BasicRadioField';
import BasicSelectField from '../../../shared/fields/BasicSelectField';
import CheckboxField from '../../fields/CheckboxField';
import { IMG_SCALE_SIZE } from '../../../../utility/constants';
import DropZoneComponent from '../../../../components/common/DropZone';
import { trackEventWithData } from '../../../utilsV3/analytics';
import { FPTI_SPEC, SCREEN } from '../../../utilsV3/fptiConstants';
import { shouldUsePayPalButtonStyle } from '../../../lib/buttonModelV3';

const renderRadioField = (props) => {
  return <BasicRadioField {...props} />;
};

const renderLanguageDropdown = (props) => {
  return <BasicSelectField {...props} />;
};

const renderCountryDropdown = (props) => {
  return <BasicSelectField {...props} />;
};

const renderCheckboxField = (props) => {
  return <CheckboxField {...props} />;
};

const ButtonStyles = ({
  formValues,
  setImageUpdated,
  dispatch,
  optionsForCountry,
  optionsForLanguage,
  dropzoneError,
  setDropzoneError,
}) => {
  const worldReady = useContext(WorldReadyContext);

  const paypalButtonStyle = new MessageFormat(worldReady, {
    id: 'buttonsV3.buttonStyles.radio.paypalButtonStyle.label',
  }).format();

  const ownButtonStyle = new MessageFormat(worldReady, {
    id: 'buttonsV3.buttonStyles.radio.ownButtonStyle.label',
  }).format();

  const largeValue = new MessageFormat(worldReady, {
    id: 'buttonsV3.buttonStyles.radio.largeValue',
  }).format();

  const smallValue = new MessageFormat(worldReady, {
    id: 'buttonsV3.buttonStyles.radio.smallValue',
  }).format();

  const buttonSizeLabel = new MessageFormat(worldReady, {
    id: 'buttonsV3.buttonStyles.buttonSize.label',
  }).format();

  const countryProperty = new MessageFormat(worldReady, {
    id: 'buttonsV3.buttonStyles.country',
  }).format();

  const languageProperty = new MessageFormat(worldReady, {
    id: 'buttonsV3.buttonStyles.language',
  }).format();

  const selectionLabel = new MessageFormat(worldReady, {
    id: 'buttonsV3.buttonStyles.selectionLabel',
  }).format();

  const cardLogoCheckbox = new MessageFormat(worldReady, {
    id: 'buttonsV3.buttonStyles.cardLogoCheckbox',
  }).format();

  const renderButtonStyleDropdown = (props) => {
    return (
      <BasicSelectField
        options={[
          { value: 'large', displayValue: largeValue },
          { value: 'small', displayValue: smallValue },
        ]}
        {...props}
      />
    );
  };

  const getValue = (key) => {
    return formValues ? formValues[key] : null;
  };

  const buttonSize = getValue('buttonSize');
  const usePaypalButtonStyle = getValue('usePaypalButtonStyle');
  const isPayPalImage = shouldUsePayPalButtonStyle({
    button_image_url: getValue('buttonImageUrl'),
  });
  const buttonImageUrl =
    isPayPalImage === 'yes' ? '' : getValue('buttonImageUrl');

  const handleImageDropzone = (image) => {
    setDropzoneError('');
    if (image.hasError) {
      setDropzoneError(image.error);
      trackEventWithData(FPTI_SPEC.FIELD_LEVEL_ERROR_IM, {
        error_code: 'BUTTON_IMAGE_DROPZONE_ERROR',
        error_desc: 'button_image',
        error_detail: image.error,
        screen: SCREEN.CONFIGURE_BUTTON,
      });
    } else {
      dispatch(change(BTN_FACTORY_V3_FORM, 'buttonImageUrl', image.src));
      setImageUpdated(true);
    }
  };

  const shouldDisableAcceptedCardLogos = () => {
    const size = getValue('buttonSize');
    return size === 'small';
  };

  return (
    <div className="button-styles">
      <BodyText strong className="wizard1-body-text">
        {selectionLabel}
      </BodyText>
      <Field
        type="radio"
        id="usePaypalButtonStyle"
        name="usePaypalButtonStyle"
        value="yes"
        className="form-radio"
        checkedValue={usePaypalButtonStyle}
        component={renderRadioField}
        label={paypalButtonStyle}
      />

      {usePaypalButtonStyle === 'yes' && (
        <div className="button-style-elements fields-margin-left">
          <Field
            id="buttonSize"
            name="buttonSize"
            className="form-dropdown"
            component={renderButtonStyleDropdown}
            label={buttonSizeLabel}
          />

          <Field
            id="countryCode"
            name="countryCode"
            className="form-dropdown"
            menuContainerClassName="dropdown-menu-height"
            component={renderCountryDropdown}
            label={countryProperty}
            options={optionsForCountry}
          />

          <Field
            id="language"
            name="language"
            className="form-dropdown"
            component={renderLanguageDropdown}
            label={languageProperty}
            options={optionsForLanguage}
          />

          {buttonSize !== 'small' && (
            <div className="show-accepted-card-logos">
              <Field
                id="showAcceptedCardLogos"
                name="showAcceptedCardLogos"
                component={renderCheckboxField}
                type="checkbox"
                label={cardLogoCheckbox}
                disabled={shouldDisableAcceptedCardLogos()}
              />
            </div>
          )}
        </div>
      )}

      <Field
        type="radio"
        id="useYourOwnButton"
        name="usePaypalButtonStyle"
        className="form-radio"
        value="no"
        checkedValue={usePaypalButtonStyle}
        component={renderRadioField}
        label={ownButtonStyle}
      />

      {usePaypalButtonStyle === 'no' && (
        <div className="fields-margin-left wizard-dropzone">
          <DropZoneComponent
            fptiSpec={{
              updated: FPTI_SPEC.CONFIGURE_BUTTON_PAGE_LOGO_UPDATED,
              change: FPTI_SPEC.CONFIGURE_BUTTON_PAGE_CHANGE_LOGO_CL,
            }}
            imgURL={buttonImageUrl}
            onChange={handleImageDropzone}
            imgWidth={IMG_SCALE_SIZE.WIDTH_STEP_1}
            showPreviewInDropzone={true}
            onlyEditButton={false}
          />
          {dropzoneError && (
            <Alert
              type="warning"
              className="dropzone-error"
              data-testid="dropzone-error"
            >
              {dropzoneError}
            </Alert>
          )}
        </div>
      )}
    </div>
  );
};

ButtonStyles.propTypes = {
  setImageUpdated: PropTypes.func,
  optionsForCountry: PropTypes.array,
  optionsForLanguage: PropTypes.array,
  dispatch: PropTypes.func,
  formValues: PropTypes.object,
  dropzoneError: PropTypes.string,
  setDropzoneError: PropTypes.func,
};

export default ButtonStyles;
